import type {
  AutomationOptions,
  AutomationPayload,
  Automation,
  Unit,
} from "../types/automation.types"
import {
  CADENCE_PRESETS,
  type CadencePreset,
  DELAY_PRESETS,
  type DelayPreset,
  HOURS_PER_WEEK,
  cadencePresetFor,
  delayPresetFor,
} from "./duration-presets"
import { UNIT_SECONDS } from "./summary"

type TimingFields = "initial_delay_value" | "initial_delay_unit" | "cadence_value" | "cadence_unit"

/**
 * UI state before serialization. Timing is edited as parts — the first send
 * as weeks/days/hours/minutes, the repeat interval as weeks/days/hours — while
 * the wire format keeps a single value + unit (a week is 7 days). A cleared
 * part is null and counts as 0.
 *
 * The presets only describe the parts (a pick fills them in), and `repeat` is
 * the "Send more than once" switch: while it is off the automation sends once
 * whatever `send_count` holds, so turning it back on restores the last total.
 */
export interface AutomationFormValues extends Omit<AutomationPayload, TimingFields | "lead_status"> {
  /**
   * Kept while the category is Nurturing, so switching back to Lead restores
   * the earlier pick; only sent for a Lead automation.
   */
  lead_status: number | null
  delay_weeks: number | null
  delay_days: number | null
  delay_hours: number | null
  delay_minutes: number | null
  delay_preset: DelayPreset
  cadence_weeks: number | null
  cadence_days: number | null
  cadence_hours: number | null
  cadence_preset: CadencePreset
  repeat: boolean
  /**
   * A stored cadence the day/hour parts cannot hold (a legacy minutes value
   * such as 90 minutes). Sent back as stored until the cadence is edited.
   */
  cadence_exact?: ExactCadence | null
}

export interface ExactCadence {
  value: number
  unit: Unit
  /** The whole hours the parts show for it. */
  hours: number
}

const MINUTE = UNIT_SECONDS.minutes
const HOUR = UNIT_SECONDS.hours
const DAY = UNIT_SECONDS.days
const WEEK = HOURS_PER_WEEK * HOUR

// The total "Total sends" starts at when the switch is first turned on.
const FIRST_REPEAT_COUNT = 2

const delayParts = (value: number, unit: Unit) => {
  const seconds = value * UNIT_SECONDS[unit]
  return {
    delay_weeks: Math.floor(seconds / WEEK),
    delay_days: Math.floor((seconds % WEEK) / DAY),
    delay_hours: Math.floor((seconds % DAY) / HOUR),
    delay_minutes: Math.floor((seconds % HOUR) / MINUTE),
  }
}

// Stored cadences are whole hours or days; a legacy minutes cadence shows its
// whole hours, never less than 1 hour (and is kept exactly, see below).
const cadenceParts = (value: number, unit: Unit) => {
  const hours = Math.max(1, Math.floor((value * UNIT_SECONDS[unit]) / HOUR))
  return {
    cadence_weeks: Math.floor(hours / HOURS_PER_WEEK),
    cadence_days: Math.floor((hours % HOURS_PER_WEEK) / 24),
    cadence_hours: hours % 24
  }
}

const exactCadence = (value: number, unit: Unit): Pick<AutomationFormValues, "cadence_exact"> => {
  if ((value * UNIT_SECONDS[unit]) % HOUR === 0) return {}
  return { cadence_exact: { value, unit, hours: cadenceTotalHours(cadenceParts(value, unit)) } }
}

export const delayTotalMinutes = (
  values: Pick<AutomationFormValues, "delay_weeks" | "delay_days" | "delay_hours" | "delay_minutes">
): number =>
  (values.delay_weeks ?? 0) * HOURS_PER_WEEK * 60 +
  (values.delay_days ?? 0) * 1440 +
  (values.delay_hours ?? 0) * 60 +
  (values.delay_minutes ?? 0)

export const cadenceTotalHours = (
  values: Pick<AutomationFormValues, "cadence_weeks" | "cadence_days" | "cadence_hours">
): number =>
  (values.cadence_weeks ?? 0) * HOURS_PER_WEEK +
  (values.cadence_days ?? 0) * 24 +
  (values.cadence_hours ?? 0)

const repeatFields = (sendCount: number) =>
  sendCount > 1
    ? { repeat: true, send_count: sendCount }
    : { repeat: false, send_count: FIRST_REPEAT_COUNT }

const timingFields = (delay: [number, Unit], cadence: [number, Unit]) => {
  const parts = { ...delayParts(...delay), ...cadenceParts(...cadence) }
  return {
    ...parts,
    delay_preset: delayPresetFor(delayTotalMinutes(parts)),
    cadence_preset: cadencePresetFor(cadenceTotalHours(parts)),
  }
}

/**
 * Create default form values from automation options: the default delay
 * (1 hour → 0d 1h 0m) and cadence (1 day → 1d 0h), one send, the current user
 * as sender, and empty copy.
 */
export const defaultFormValues = (options: AutomationOptions): AutomationFormValues => ({
  name: "",
  category: "LEAD",
  channel: options.defaults.channel,
  lead_status: null,
  ...timingFields(
    [options.defaults.initial_delay_value, options.defaults.initial_delay_unit],
    [options.defaults.cadence_value, options.defaults.cadence_unit]
  ),
  ...repeatFields(options.defaults.send_count),
  source_template: null,
  subject: "",
  body: "",
  sender_user: options.defaults.sender_user,
})

/**
 * Convert a saved automation record to form values.
 *
 * - sender_user can be null (the user no longer exists) — maps to empty string
 * - cadence is null when send_count was 1 at save time — backfilled to 1 day
 *   so the form shows a sensible default if the switch is later turned on
 */
export const recordToFormValues = (automation: Automation): AutomationFormValues => ({
  name: automation.name,
  category: automation.category ?? "LEAD",
  channel: automation.channel,
  lead_status: automation.lead_status,
  ...timingFields(
    [automation.initial_delay_value, automation.initial_delay_unit],
    [automation.cadence_value ?? 1, automation.cadence_unit ?? "days"]
  ),
  ...(automation.cadence_value != null && automation.cadence_unit
    ? exactCadence(automation.cadence_value, automation.cadence_unit)
    : {}),
  ...repeatFields(automation.send_count),
  source_template: automation.source_template,
  subject: automation.subject,
  body: automation.body,
  sender_user: automation.sender_user ?? "",
})

/**
 * The parts a preset pick fills in, or null when the change picks Custom (the
 * parts keep their values and become editable) or touches no preset.
 */
export const timingPatch = (
  changed: Partial<AutomationFormValues>
): Partial<AutomationFormValues> | null => {
  const delay = DELAY_PRESETS.find((preset) => preset.value === changed.delay_preset)
  if (delay) return delayParts(delay.minutes, "minutes")
  const cadence = CADENCE_PRESETS.find((preset) => preset.value === changed.cadence_preset)
  if (cadence) return cadenceParts(cadence.hours, "hours")
  return null
}

/**
 * Convert form values to a payload ready for the API: the delay as total
 * minutes, the cadence as total hours (a single send with no cadence while
 * the switch is off; a legacy exact cadence as stored while its parts still
 * show it), the name trimmed. A Nurturing automation has no trigger state, so
 * its payload carries no `lead_status` key at all.
 */
export const formValuesToPayload = ({
  delay_weeks,
  delay_days,
  delay_hours,
  delay_minutes,
  delay_preset: _delayPreset,
  cadence_weeks,
  cadence_days,
  cadence_hours,
  cadence_preset: _cadencePreset,
  cadence_exact,
  repeat,
  lead_status,
  ...rest
}: AutomationFormValues): AutomationPayload => {
  const hours = cadenceTotalHours({ cadence_weeks, cadence_days, cadence_hours })
  const exact = cadence_exact && cadence_exact.hours === hours ? cadence_exact : null
  return {
    ...rest,
    ...(rest.category === "NURTURING" ? {} : { lead_status }),
    name: rest.name.trim(),
    send_count: repeat ? rest.send_count : 1,
    initial_delay_value: delayTotalMinutes({ delay_weeks, delay_days, delay_hours, delay_minutes }),
    initial_delay_unit: "minutes",
    cadence_value: repeat ? (exact?.value ?? hours) : null,
    cadence_unit: repeat ? (exact?.unit ?? "hours") : null,
  }
}

const CADENCE_FIELDS: (keyof AutomationFormValues)[] = [
  "cadence_preset",
  "cadence_weeks",
  "cadence_days",
  "cadence_hours",
]

// Any edit to the repeat interval replaces a legacy exact cadence.
export const touchesCadence = (changed: Partial<AutomationFormValues>) =>
  CADENCE_FIELDS.some((field) => field in changed)

// What the backend compares to decide whether an edit replaces a Nurturing
// automation's upcoming runs: the category, the delay, the cadence (only when
// repeating) and the total sends, durations compared as seconds.
const scheduleShape = (automation: {
  category: AutomationPayload["category"]
  initial_delay_value: number
  initial_delay_unit: Unit
  cadence_value: number | null
  cadence_unit: Unit | null
  send_count: number
}) => [
  automation.category,
  automation.initial_delay_value * UNIT_SECONDS[automation.initial_delay_unit],
  automation.send_count > 1 && automation.cadence_value != null && automation.cadence_unit
    ? automation.cadence_value * UNIT_SECONDS[automation.cadence_unit]
    : null,
  automation.send_count
]

/** Whether saving these values would restart the saved automation's schedule from the save. */
export const restartsSchedule = (saved: Automation, values: AutomationFormValues): boolean =>
  scheduleShape({ ...saved, category: saved.category ?? "LEAD" }).join() !==
  scheduleShape(formValuesToPayload(values)).join()

/**
 * What saving these values does to the saved automation's Nurturing sends, as
 * the backend does it: a timing change cancels the pending runs, and an
 * enabled Nurturing automation gets a fresh schedule from the save (a paused
 * one when it is resumed). A draft has no runs; a text-only edit keeps them.
 */
export type ScheduleWarning = "restart" | "restartOnResume" | "cancel"

export const scheduleWarning = (
  saved: Automation,
  values: AutomationFormValues
): ScheduleWarning | null => {
  if (!saved.enabled_at || !restartsSchedule(saved, values)) return null
  const wasNurturing = saved.category === "NURTURING"
  const isNurturing = values.category === "NURTURING"
  if (wasNurturing && !isNurturing) return "cancel"
  if (saved.is_enabled && isNurturing) return "restart"
  if (wasNurturing) return "restartOnResume"
  return null
}
